import { supabase } from '../lib/supabase/client';
import { Database } from '../types/database.types';

type InquiryInsert = Database['public']['Tables']['contact_inquiries']['Insert'];
type Inquiry = Database['public']['Tables']['contact_inquiries']['Row'];

interface InquiryResponse {
  data: any | null;
  error: Error | null;
}

export const inquiryService = {
  async create(inquiry: InquiryInsert): Promise<InquiryResponse> {
    try {
      const { data, error } = await supabase
        .from('contact_inquiries')
        .insert({
          inquiry_type: inquiry.inquiry_type,
          company_name: inquiry.company_name,
          contact_person: inquiry.contact_person,
          email: inquiry.email,
          phone: inquiry.phone,
          country: inquiry.country,
          product_category: inquiry.product_category,
          quantity: inquiry.quantity || null,
          frequency: inquiry.frequency || null,
          message: inquiry.message,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message?.includes('Failed to fetch') || 
            error.message?.includes('NetworkError')) {
          return { 
            data: null, 
            error: new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.') 
          };
        }
      }
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to submit inquiry') 
      };
    }
  },

  // Admin-specific operations
  async getAll(filters?: {
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    country?: string;
    productCategory?: string;
    searchQuery?: string;
  }): Promise<InquiryResponse> {
    try {
      let query = supabase
        .from('contact_inquiries')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      if (filters?.country) {
        query = query.eq('country', filters.country);
      }

      if (filters?.productCategory) {
        query = query.eq('product_category', filters.productCategory);
      }

      if (filters?.searchQuery) {
        query = query.or(`company_name.ilike.%${filters.searchQuery}%,email.ilike.%${filters.searchQuery}%,contact_person.ilike.%${filters.searchQuery}%`);
      }

      const { data, error } = await query;

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message?.includes('Failed to fetch') || 
            error.message?.includes('NetworkError')) {
          return { 
            data: null, 
            error: new Error('Cannot connect to database. Your Supabase project may be paused or deleted.') 
          };
        }
      }
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to fetch inquiries') 
      };
    }
  },

  async getById(id: string): Promise<InquiryResponse> {
    try {
      const { data, error } = await supabase
        .from('contact_inquiries')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message?.includes('Failed to fetch') || 
            error.message?.includes('NetworkError')) {
          return { 
            data: null, 
            error: new Error('Cannot connect to database. Your Supabase project may be paused or deleted.') 
          };
        }
      }
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to fetch inquiry') 
      };
    }
  },

  async updateStatus(id: string, status: string): Promise<InquiryResponse> {
    try {
      const { data, error } = await supabase
        .from('contact_inquiries')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message?.includes('Failed to fetch') || 
            error.message?.includes('NetworkError')) {
          return { 
            data: null, 
            error: new Error('Cannot connect to database. Your Supabase project may be paused or deleted.') 
          };
        }
      }
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to update inquiry status') 
      };
    }
  },

  async bulkUpdateStatus(ids: string[], status: string): Promise<InquiryResponse> {
    try {
      const { data, error } = await supabase
        .from('contact_inquiries')
        .update({ status })
        .in('id', ids)
        .select();

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message?.includes('Failed to fetch') || 
            error.message?.includes('NetworkError')) {
          return { 
            data: null, 
            error: new Error('Cannot connect to database. Your Supabase project may be paused or deleted.') 
          };
        }
      }
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to update inquiries') 
      };
    }
  },

  async delete(id: string): Promise<InquiryResponse> {
    try {
      const { error } = await supabase
        .from('contact_inquiries')
        .delete()
        .eq('id', id);

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      return { data: { success: true }, error: null };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message?.includes('Failed to fetch') || 
            error.message?.includes('NetworkError')) {
          return { 
            data: null, 
            error: new Error('Cannot connect to database. Your Supabase project may be paused or deleted.') 
          };
        }
      }
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to delete inquiry') 
      };
    }
  },

  async getMetrics(): Promise<InquiryResponse> {
    try {
      const { data: allInquiries, error: allError } = await supabase
        .from('contact_inquiries')
        .select('status, created_at');

      if (allError) {
        return { data: null, error: new Error(allError.message) };
      }

      const metrics = {
        total: allInquiries?.length || 0,
        pending: allInquiries?.filter((i: any) => i.status === 'pending')?.length || 0,
        inProgress: allInquiries?.filter((i: any) => i.status === 'in-progress')?.length || 0,
        quoted: allInquiries?.filter((i: any) => i.status === 'quoted')?.length || 0,
        completed: allInquiries?.filter((i: any) => i.status === 'completed')?.length || 0,
        closed: allInquiries?.filter((i: any) => i.status === 'closed')?.length || 0,
        conversionRate: allInquiries?.length > 0 
          ? ((allInquiries?.filter((i: any) => i.status === 'completed')?.length || 0) / allInquiries.length * 100).toFixed(1)
          : '0.0',
        thisMonth: allInquiries?.filter((i: any) => {
          const created = new Date(i.created_at);
          const now = new Date();
          return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
        })?.length || 0
      };

      return { data: metrics, error: null };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message?.includes('Failed to fetch') || 
            error.message?.includes('NetworkError')) {
          return { 
            data: null, 
            error: new Error('Cannot connect to database. Your Supabase project may be paused or deleted.') 
          };
        }
      }
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Failed to fetch metrics') 
      };
    }
  }
};